import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';

import { useMessage } from '@/modules/common/application/context/message';
import useEnhancedQuery from '@/modules/common/application/use-enhanced-query';
import {
  EnhancedEsgCategory,
  EsgCategoryLevel,
  FeatureFlag,
  TranslatedCategoryType,
} from '@/modules/common/types';
import { GET_ESG_CATEGORIES } from '@/services/graphql/queries';

import { isAlignedWithGlobalKpiFeatureFlag } from './global-kpi';
import { genericSortSingleColumn } from './sort';
import { removeUnderscoreToCamelCase } from './string';

const useCategory = (isGlobalView: boolean) => {
  const { t } = useTranslation();
  const { setErrorMessage } = useMessage();
  const isFacilityLevelEnabled = useFeatureFlagEnabled(FeatureFlag.GLOBAL_KPIS_FACILITY_LEVEL);

  const { loading, data } = useEnhancedQuery<{ esgCategories: EnhancedEsgCategory[] }>(
    GET_ESG_CATEGORIES,
    {
      fetchPolicy: 'cache-first',
      variables: {
        categoryLevel: isGlobalView ? EsgCategoryLevel.COMPANY : EsgCategoryLevel.FACILITY,
      },
      onError: () => setErrorMessage(t('messages.generic.error')),
    }
  );

  const categories: Record<string, EnhancedEsgCategory> = {};
  if (data?.esgCategories) {
    for (const category of data.esgCategories) {
      if (isAlignedWithGlobalKpiFeatureFlag(isFacilityLevelEnabled, category.id))
        categories[category.id] = category;
    }
  }

  const getCategoryById = (categoryId: string): EnhancedEsgCategory | undefined => {
    return categories[categoryId];
  };

  const getCategoryByTitle = (title: string): EnhancedEsgCategory | undefined => {
    return data?.esgCategories?.find((category) => category.title === title);
  };

  const sortCategoryArray = (categories: EnhancedEsgCategory[]) => {
    const translatedCategories: TranslatedCategoryType[] = categories.reduce(
      (acc: TranslatedCategoryType[], category: EnhancedEsgCategory) => {
        if (isAlignedWithGlobalKpiFeatureFlag(isFacilityLevelEnabled, category.id))
          acc.push({
            ...category,
            translatedTitle: t(
              'shared.types.esgCategory.' + removeUnderscoreToCamelCase(category.title)
            ),
          });

        return acc;
      },
      []
    );
    genericSortSingleColumn(translatedCategories, 'translatedTitle');
    return translatedCategories.map(({ area, id, title, reportChapters }) => ({
      area,
      id,
      title,
      reportChapters,
    })) as EnhancedEsgCategory[];
  };

  return {
    categoriesArray: data?.esgCategories ? sortCategoryArray(data.esgCategories) : [],
    categories,
    loading,
    getCategoryById,
    getCategoryByTitle,
  };
};

export default useCategory;
